<template>
    <v-dialog v-model="dialog" max-width="600px" scrollable content-class="z-full-width-dialog">
        <v-card>
            <v-card-title class="headline flex-column pa-2">
                <div class="d-flex fill-width">
                    <div class="flex-grow-1">{{$translate('Invite players')}}</div>
                    <v-btn icon @click="dialog = false" class="float-right"><v-icon>mdi-close</v-icon></v-btn>
                </div>
            </v-card-title>

            <v-card-text>
                <div v-if="loadingStatus.loading">
                    <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
                </div>
                <div v-else-if="!invitelink">
                    <div v-if="group.type !== 'family'">{{$translate('In order to invite someone to the team, create an invitation link')}}</div>
                    <div v-else>{{$translate('In order to invite someone to the group, create an invitation link')}}</div>
                    <v-btn color="primary" @click="createInviteLink" :loading="loadingStatus.creating">{{$translate('Create invitation link')}}</v-btn>
                </div>

                <div v-else>
                    <p v-if="group.type !== 'family'">{{$translate('Share the following link to those who you wish to invite to the team')}}</p>
                    <p v-else>{{$translate('Share the following link to those who you wish to invite to the group')}}</p>
                    <div>
                        <v-text-field
                            ref="inviteLinkField"
                            v-model="invitelink"
                            :label="$translate('Invitation link')"
                            readonly
                            outlined
                            :append-outer-icon="copied ? 'mdi-check' : 'mdi-content-copy'"
                            :color="copied ? 'success' : 'primary'"
                            @click:append-outer="copyInviteLink"
                            @focus="selectInviteLink"
                        >
                            <template v-slot:append-outer>
                                <v-tooltip bottom v-if="copied">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" color="success">
                                            mdi-check
                                        </v-icon>
                                    </template>
                                    {{$translate('Copied!')}}
                                </v-tooltip>
                                <v-icon v-else @click="copyInviteLink" color="primary">
                                    mdi-content-copy
                                </v-icon>
                            </template>
                        </v-text-field>
                    </div>
                    <v-btn color="error" outlined @click="removeInviteLink" :loading="loadingStatus.removing">{{$translate('Remove invitation link')}}</v-btn>

                    <br><br>

                    <div>{{$translate('You can send an invitation with e-mail')}}</div>

                    <v-form v-model="formValid">
                        <form-field :field="recipientsField" v-model="recipients" star />
                    </v-form>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn v-if="invitelink" color="primary" @click="submit" :disabled="!canSubmit()" :loading="loadingStatus.inviting">{{$translate('Invite')}}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import FormField from '../components/FormField.vue';
import { isValidMail } from '../util.js';
export default {
    name: 'zoezi-teamsport-invite-new-players-dialog',

    props: {
        value: {
            type: Boolean,
            default: false
        },
        group: {
            type: Object
        }
    },

    components: {
        FormField
    },

    data: () => ({
        invitelink: null,
        loadingStatus: {
            loading: false,
            creating: false,
            removing: false,
            inviting: false
        },
        formValid: false,
        recipientsField: {type: 'textarea', title: 'E-mail addresses', required: true, rules: []},
        recipients: '',
        copied: false
    }),

    computed: {
        dialog: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        hasClipboard() {
            return navigator.clipboard && window.isSecureContext;
        }
    },
    methods: {
        possiblyShowError(response) {
            if (!response.ok) {
                response.json().then(data => {
                    this.$store.commit('showErrorDialog', {title: 'Fel', text: 'Något gick fel. ' + this.$translate(data.message)});
                });
            }
        },
        selectInviteLink() {
            this.$nextTick(() => {
                const input = this.$refs.inviteLinkField.$el.querySelector('input');
                if (input && input.select) {
                    input.select();
                }
            });
        },
        copyInviteLink() {
            if (this.hasClipboard) {
                navigator.clipboard.writeText(this.invitelink).then(() => {
                    this.copied = true;
                    setTimeout(() => this.copied = false, 3000);
                }, () => {
                    this.$store.commit('showSnackBar', {text: this.$translate('Failed to copy')});
                });
            }
        },
        getInviteLink() {
            this.loadingStatus.loading = true;
            this.$api.get('/api/memberapi/group/get_invite_link', { group_id: this.group.id }).then(data => {
                if (data.key) {
                    const path = this.$root.siteconfig.pages.mypages || '/';
                    const tab = this.$root.siteconfig.pages.mypagesTab || 'tab';
                    const inviteParam = this.$root.siteconfig.pages.mypagesGroupInvite || 'groupinvite';
                    const hostname = window.location.host;
                    this.invitelink = `https://${hostname}${path}?${tab}=teamsport&${inviteParam}=${data.key}`;
                } else {
                    this.invitelink = null;
                }
                this.loadingStatus.loading = false;
            })
                .catch(() => {
                    this.invitelink = null;
                    this.loadingStatus.loading = false;
                });
        },
        createInviteLink() {
            this.loadingStatus.creating = true;
            this.$api.post('/api/memberapi/group/create_invite_link', null, { group_id: this.group.id }).then(response => {
                this.loadingStatus.creating = false;
                this.getInviteLink();
                this.possiblyShowError(response)
            });
        },
        removeInviteLink() {
            this.loadingStatus.removing = true;
            this.$api.post('/api/memberapi/group/remove_invite_link', null, { group_id: this.group.id }).then(response => {
                this.loadingStatus.removing = false;
                if (response.ok) {
                    this.invitelink = null;
                }
                this.possiblyShowError(response)
            });
        },
        mailRule(x) {
            if (x && x.split(/[\s,]+/).every(isValidMail)) {
                return true;
            }

            return this.$translate('Invalid e-mail list');
        },
        canSubmit() {
            return this.formValid;
        },
        submit() {
            let data = {
                group_id: this.group.id,
                recipients: this.recipients
            };
            this.loadingStatus.inviting = true;
            this.$api.post('/api/memberapi/group/invite', data).then(response => {
                this.loadingStatus.inviting = false;
                this.dialog = false;
                this.possiblyShowError(response)
            })
        }
    },
    mounted() {
        this.getInviteLink();
        this.recipientsField.rules = [this.mailRule];
    }
};
</script>
